import React from "react";
import {
  Button,
  Body1,
  Card,
  CardHeader,
  Caption1,
  List,
  ListItem,
  Text,
  Menu,
  MenuTrigger,
  MenuPopover,
  MenuList,
  MenuItem,
  tokens,
  makeStyles,
  makeResetStyles,
  mergeClasses,
} from "@fluentui/react-components";
import { AppsListRegular, Delete20Regular } from "@fluentui/react-icons";
import { useStore } from "../index";
import { IClausionStateStore } from "../../zustand/store";
import { useShallow } from 'zustand/shallow'
import { useTranslation } from 'react-i18next';
import globalStyles from "./CommonStyle";
const useListItemRootStyles = makeResetStyles({
  gridTemplate: `"preview preview preview" auto
      "header action secondary_action" auto / 1fr auto auto
    `,
});

const useStyles = makeStyles({
  list: {
    display: "flex",
    flexDirection: "column",
    gap: "4px", // Gap between list items (boxes)
  },
  listItem: {
    display: "flex",
    flexDirection: "column", // Stack header and action vertically
    gap: "8px", // Vertical gap between the header (title-caption) and action (button)
    
  },
  caption: {
    color: tokens.colorNeutralForeground3,
  },
  title: {
    color: tokens.colorNeutralForeground1,
    fontWeight: 600,
    display: "block",
  },
  preview: { gridArea: "preview", overflow: "hidden" },
  header: { 
    width: "100%", // Ensure the header takes full width
  },
  action: { 
    display: "flex",
    alignItems: "center", // Ensure the button and delete icon are centered vertically
    width: "100%", // Ensure the action takes full width
    gap: "8px", // Add a small gap between the button and the delete icon
  },
  buttonStyle: {
    width: '242px',
    height: '32px',
    flexGrow: 1,
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'center',
    alignItems: 'center',
    padding: '0 12px',
    borderRadius: '3px',
    border: 'solid 1px #b8c5d5',
    backgroundColor: '#ffffff',
    color: '#546176',
    ":hover": {
      backgroundColor: '#ffffff',
      color: '#546176',
      border: 'solid 1px #b8c5d5',
    },
    ":active": {
      backgroundColor: '#ffffff', 
    },
    fontWeight: 'normal',
  },
  deleteButtonStyle: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '32px', // Match the height of the button for a square icon button
    height: '32px',
    borderRadius: '3px',
    border: 'solid 1px #b8c5d5',
    backgroundColor: '#ffffff',
    color: '#546176',
    ":hover": {
      backgroundColor: '#ffffff',
      color: '#546176',
      border: 'solid 1px #b8c5d5',
    },
    ":active": {
      backgroundColor: '#ffffff', 
    },
    marginLeft: "auto",
    float:"left",
  },
  serviceUrlBoxStyle: {
    alignSelf: 'stretch',
    flexGrow: 0,
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'flex-start',
    alignItems: 'flex-start',
    gap: '4px',
    padding: '8px 16px', // Uniform padding, no extra space for secondaryAction
    borderRadius: '3px',
    border: 'solid 1px #b8c5d5',
    backgroundColor: '#ffffff',
    boxSizing: 'border-box',
  },
  
});

const ServiceItem = (props: { service: { url: string; addedAt: Date; }; }) => {
  const { t } = useTranslation();
  const delRecentServiceUrl = useStore(useShallow((state: IClausionStateStore) => state.delRecentServiceUrl));
  const setServiceUrl = useStore(useShallow((state: IClausionStateStore) => state.setServiceUrl));

  const listItemStyles = useListItemRootStyles();
  const styles = useStyles();
  const globalStyle=globalStyles();
  return (
    <ListItem
      value={props.service.url}
      className={mergeClasses(styles.listItem)}
    >
      <div role="gridcell" className={mergeClasses(styles.header, styles.serviceUrlBoxStyle)}>
        <Text className={mergeClasses(globalStyle.fontStyle, globalStyle.font14)}>{props.service.url} ({t('recentserviceurls_connected')} {props.service.addedAt.toLocaleString(navigator.language)})</Text>
        {/* <Caption1 className={styles.caption}>({t('recentserviceurls_connected')} {props.service.addedAt.toLocaleString(navigator.language)})</Caption1> */}
      </div>
      <div role="gridcell" className={styles.action}>
        <Button
          aria-label="Connect"
          onClick={() => {
            setServiceUrl(props.service.url);
          }}
          className={mergeClasses(styles.buttonStyle, globalStyle.font14)}
        >
          {t('recentserviceurls_set_url')}
        </Button>
        <Button
          appearance="transparent"
          icon={<Delete20Regular />}
          onClick={() => {
            delRecentServiceUrl(props.service.url);
          }}
          className={styles.deleteButtonStyle}
          aria-label="Delete service URL"
        />
      </div>
    </ListItem>
  );
};

const RecentServiceUrlsCard: React.FC = () => {
  const { t } = useTranslation();
  const globalStyle= globalStyles();
  const recentServiceUrls = useStore(useShallow((state: IClausionStateStore) => state.recentServiceUrls));
  return (
    <Card className={globalStyle.cardContainer}>
      <CardHeader
        header={
          <Body1>
            <AppsListRegular />
              <span className={mergeClasses(globalStyle.fontStyle, globalStyle.font16)}> {t('recentserviceurls_title')}</span>
          </Body1>
        }
      />
      <List className={useStyles().list} navigationMode="composite">
        {recentServiceUrls.map((service, index) => (
          <ServiceItem key={"service_"+index} service={service} />
        ))}
      </List>
    </Card>
  );

      
};

export default RecentServiceUrlsCard;
